import Alpine from "alpinejs";
import Swiper from 'swiper';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';

Alpine.data('quickLinks', () => ({
    swiper: null,
    activeItem: 0,
    init() {
        const swiperEl = this.$refs.carousel;
        const wrapperEl = swiperEl.querySelector('.swiper-wrapper');
        const slideEls = swiperEl.querySelectorAll('.swiper-slide');

        // Duplicate each slide and append it to the wrapper to allow looping
        let dupeCount = 1;
        if(this.$refs.carousel.classList.contains('--medium') && slideEls.length === 3) dupeCount = 2;

        for (let i = 0; i < dupeCount; i++) {
            slideEls.forEach(slide => {
                const clonedSlide = slide.cloneNode(true);
                wrapperEl.appendChild(clonedSlide);
            });
        }

        this.swiper = new Swiper(this.$refs.carousel, {
            modules: [Autoplay],
            autoplay: {
                delay: 1,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
            speed: 10000,
            slidesPerView: 'auto',
            spaceBetween: 24,
            freeMode: true,
            autoHeight: false,
            loop: true,
        });
    }
}));

Alpine.data('productSelector', () => ({
    swiper: null,
    thumbnails: null,
    activeItem: 0,
    init() {
        this.swiper = new Swiper(this.$refs.carousel, {
            slidesPerView: 1,
            spaceBetween: 30,
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            modules: [Navigation, Pagination, EffectFade],
            navigation: {
                hiddenClass: 'hidden',
                nextEl: '.productNext',
                prevEl: '.productPrev',
            },
            pagination: {
                el: this.$refs.pagination,
                clickable: true,
                renderBullet: function (index, className) {
                    return '<span class="' + className + '">' + (index + 1) + "</span>";
                },
            },
            watchSlidesProgress: true
        });
        
        this.initThumbnails();
        
        this.swiper.on('slideChange', () => {
            this.activeItem = this.swiper.realIndex;
            this.updateActiveThumbnail();
            this.updateTheme();
            this.syncAnimations();
        });

        this.updateTheme();
        // The slides' lottie() components initialise after this init() returns, so defer the first sync.
        this.$nextTick(() => this.syncAnimations());
    },

    /**
     * Play the active slide's product animation and pause the rest: every slide is in the DOM and
     * near the viewport (fade effect), so without this each hidden slide would animate too.
     */
    syncAnimations() {
        this.swiper.slides.forEach((slide, index) => {
            slide.querySelectorAll('.product-selector__animation').forEach((el) => {
                const lottie = Alpine.$data(el);

                if (typeof lottie?.play !== 'function') {
                    return;
                }

                if (index === this.swiper.activeIndex) {
                    lottie.play();
                } else {
                    lottie.pause();
                }
            });
        });
    },

    updateTheme() {
        const activeSlide = this.swiper.slides[this.swiper.activeIndex];

        if (!activeSlide) {
            return;
        }

        const backgroundColour = activeSlide.dataset.bgColour;
        const accentColour = activeSlide.dataset.accentColour;

        if (backgroundColour) {
            this.$refs.carousel.style.setProperty('--product-selector-bg', backgroundColour);
            this.$refs.carousel.style.backgroundColor = backgroundColour;
        }

        if (accentColour) {
            this.$refs.carousel.style.setProperty('--product-selector-accent', accentColour);
            this.$refs.carousel.style.color = accentColour;
        }
    },
    
    initThumbnails() {
        this.thumbnails = this.$refs.thumbnailPagination.querySelectorAll('.product-selector__thumbnail');
        
        this.thumbnails.forEach((thumb, index) => {
            thumb.addEventListener('click', () => {
                this.swiper.slideTo(index);
                this.activeItem = index;
                this.updateActiveThumbnail();
            });
        });
        
        this.updateActiveThumbnail();
    },
    
    updateActiveThumbnail() {
        this.thumbnails.forEach((thumb, index) => {
            index === this.activeItem ? thumb.classList.add('--active') : thumb.classList.remove('--active');
        });
    }
}));

Alpine.data('timeline', () => ({
    swiper: null,
    paginationItems: null,
    marker: null,
    activeItem: 0,
    init() {
        this.swiper = new Swiper(this.$refs.carousel, {
            slidesPerView: 1,
            spaceBetween: 30,
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            modules: [Navigation, EffectFade],
            navigation: {
                hiddenClass: 'hidden',
                nextEl: '.timelineNext',
                prevEl: '.timelinePrev',
            },
            watchSlidesProgress: true
        });
        
        this.initPagination();
        
        this.swiper.on('slideChange', () => {
            this.activeItem = this.swiper.realIndex;
            this.updateActiveItem();
            this.positionMarker();
        });
    },
    
    initPagination() {
        this.paginationItems = this.$refs.pagination.querySelectorAll('.timeline__pagination-item');
        
        this.paginationItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                this.swiper.slideTo(index);
                this.activeItem = index;
                this.updateActiveItem();
            });
        });
        
        this.updateActiveItem();
    },
    
    updateActiveItem() {
        this.paginationItems.forEach((item, index) => {
            index === this.activeItem ? item.classList.add('--active') : item.classList.remove('--active');
        });
    },
    positionMarker() {
        const pagination = this.$refs.pagination;
        this.marker = pagination.querySelector('.timeline__pagination-marker');
        const activeItem = pagination.querySelector('.timeline__pagination-item.--active');
        
        if (!activeItem || !this.marker) return;
        
        // Calc position
        const itemRect = activeItem.getBoundingClientRect();
        const paginationRect = pagination.getBoundingClientRect();
        const markerWidth = this.marker.offsetWidth;
        const itemWidth = activeItem.offsetWidth;
        
        const leftPosition = (itemRect.left - paginationRect.left) + (itemWidth / 2) - (markerWidth / 2);
        
        this.marker.style.transform = `translateX(${leftPosition}px)`;
    },
}));

Alpine.data('newsSocialFeed', () => ({
    swiper: null,
    init() {

        this.swiper = new Swiper(this.$refs.carousel, {
            slidesPerView: 'auto',
            spaceBetween: 24,
            modules: [Navigation, EffectFade],
            navigation: {
                hiddenClass: 'hidden',
                nextEl: '.news-social-feed__carousel-nav .btn--next',
                prevEl: '.news-social-feed__carousel-nav .btn--prev',
            },
            freeMode: true,
            autoHeight: false,
            loop: false
        });
    }
}));